/*
This component communicates with the site framing the widget to resize the frame to the exact widget size
  1. When the parent window is loaded / resized - parent will post window dimensions, this component listens for the dimensions
  2. Based on the parent window dimensions & the open/closed state of the widget - this component will figure out what size the widget should be
  3. The component will post the calculated widget size to the parent window, which will resize the frame appropriately
*/
import React, { useState, useEffect, useContext } from 'react';
import { MobileStates, WidgetStates } from '../Widget';
import '../../utils/Flows';
import { frameAncestorsToPostMessageOrigins, isAllowedOrigin, isLocalDev, } from '../../../clearstep/util/AppHelpers';
import { PartnerCustomizationContext } from '../../../tailwind_clearstep/Partners/symptom_check/PartnerCustomizations';
import { THEME } from '../../../clearstep/util/Theme';
export var WidgetResizer = function (_a) {
    var workflow = _a.workflow, widgetState = _a.widgetState, iconPath = _a.iconPath, children = _a.children, mobileState = _a.mobileState;
    var _b = useState(1080), parentWindowHeight = _b[0], setParentWindowHeight = _b[1];
    var _c = useState(1920), parentWindowWidth = _c[0], setParentWindowWidth = _c[1];
    var _d = useState(0), widgetWidth = _d[0], setWidgetWidth = _d[1];
    var _e = useState(0), widgetHeight = _e[0], setWidgetHeight = _e[1];
    /* CONSTANTS for size calculation */
    var numPixelsRightWidget = mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED ? 0 : 20; // #contentPosition right-5
    var numPixelsBottomWidget = mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED ? 0 : 20; // #contentPosition bottom-5
    var buttonHeight = mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED ? 0 : 60;
    var buttonWidth = mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED ? 0 : 60;
    var spaceBetweenButtonWidget = mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED ? 0 : 20;
    var halfCollapsedLogoHeight = iconPath ? 25 : 0;
    // ensure shadow doesn't abrubtly cut off
    var additionalLeftForShadow = 38;
    var additionalTopForShadow = 38;
    // from #widget-content tailwind utilities, must keep in sync
    var widgetWidthHidden = 0;
    var widgetHeightHidden = 0;
    var widgetWidthCollapsed = 224;
    var widgetHeightCollapsed = 96;
    var widgetWidthChooseFlow = 368;
    var widgetHeightChooseFlow = 616;
    var widgetWidthConvoOpen = 460;
    var widgetHeightConvoOpen = 620;
    var partnerCustomizations = useContext(PartnerCustomizationContext);
    var getAlwaysAllowedOrigins = function () {
        var alwaysAllowedOrigins = ['https://widget.qa01-eks-01.clearstep.health'];
        if (isLocalDev()) {
            alwaysAllowedOrigins = alwaysAllowedOrigins.concat('http://localhost:3000');
        }
        return alwaysAllowedOrigins;
    };
    // Update state when parent posts a new height/width (due to parent window resizing)
    useEffect(function () {
        var _a, _b;
        var alwaysAllowedOrigins = getAlwaysAllowedOrigins();
        var themeOrigins = (_b = (_a = window.theme) === null || _a === void 0 ? void 0 : _a.frameAncestors) === null || _b === void 0 ? void 0 : _b.split(' ');
        var allowedOrigins = alwaysAllowedOrigins.concat(themeOrigins);
        var handleMessage = function (e) {
            if (isAllowedOrigin(e.origin, allowedOrigins)) {
                var w = e.data.parentWindowWidth;
                var h = e.data.parentWindowHeight;
                if (w || h) {
                    if (w) {
                        setParentWindowWidth(w);
                    }
                    if (h) {
                        setParentWindowHeight(h);
                    }
                }
            }
            else if (e.data.parentWindowWidth || e.data.parentWindowHeight) {
                console.error("Widget origin not whitelisted: ".concat(e.origin, ", partner: ").concat(THEME.partner));
            }
        };
        window.addEventListener('message', handleMessage);
        return function () {
            window.removeEventListener('message', handleMessage);
        };
    }, []);
    // Get widget dimensions when window is full sized
    var getFullScreenWidgetDimensions = function () {
        var fullScreenWidgetWidth = 0;
        var fullScreenWidgetHeight = 0;
        if (widgetState === WidgetStates.HIDDEN) {
            fullScreenWidgetWidth =
                widgetWidthHidden + numPixelsRightWidget + buttonWidth + additionalLeftForShadow;
            fullScreenWidgetHeight =
                widgetHeightHidden + numPixelsBottomWidget + buttonHeight + additionalTopForShadow;
        }
        if (widgetState === WidgetStates.COLLAPSED) {
            fullScreenWidgetWidth = widgetWidthCollapsed + numPixelsRightWidget + additionalLeftForShadow;
            fullScreenWidgetHeight =
                numPixelsBottomWidget +
                    buttonHeight +
                    spaceBetweenButtonWidget +
                    widgetHeightCollapsed +
                    halfCollapsedLogoHeight +
                    additionalTopForShadow;
        }
        if (widgetState === WidgetStates.EXPANDED && !workflow) {
            fullScreenWidgetWidth =
                widgetWidthChooseFlow + numPixelsRightWidget + additionalLeftForShadow;
            fullScreenWidgetHeight =
                widgetHeightChooseFlow +
                    numPixelsBottomWidget +
                    buttonHeight +
                    spaceBetweenButtonWidget +
                    additionalTopForShadow;
        }
        if (widgetState === WidgetStates.EXPANDED && workflow) {
            fullScreenWidgetWidth = widgetWidthConvoOpen + numPixelsRightWidget + additionalLeftForShadow;
            fullScreenWidgetHeight =
                widgetHeightConvoOpen +
                    numPixelsBottomWidget +
                    buttonHeight +
                    spaceBetweenButtonWidget +
                    additionalTopForShadow;
        }
        return { fullScreenWidgetWidth: fullScreenWidgetWidth, fullScreenWidgetHeight: fullScreenWidgetHeight };
    };
    useEffect(function () {
        var _a;
        var targetOrigins = (_a = partnerCustomizations.postMessageRecipients()) === null || _a === void 0 ? void 0 : _a.requestWidgetDimensionsOnLoad;
        if (targetOrigins && Array.isArray(targetOrigins)) {
            targetOrigins === null || targetOrigins === void 0 ? void 0 : targetOrigins.forEach(function (targetOrigin) {
                try {
                    window.parent.postMessage({ type: 'request', status: 'Request Window Dimensions' }, targetOrigin);
                }
                catch (e) {
                    /*
                      Note postMessage will fail in all cases but 1 correct origin,,
                      because we are posting to ALL origins
                    */
                }
            });
        }
    }, []);
    // Update what widgetHeight/widgetWidth should be based on dimensions and open/closed state
    useEffect(function () {
        var _a = getFullScreenWidgetDimensions(), fullScreenWidgetWidth = _a.fullScreenWidgetWidth, fullScreenWidgetHeight = _a.fullScreenWidgetHeight;
        var w = fullScreenWidgetWidth;
        var h = fullScreenWidgetHeight;
        if ((mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED) ||
            fullScreenWidgetWidth > parentWindowWidth) {
            w = parentWindowWidth;
        }
        if ((mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED) ||
            fullScreenWidgetHeight > parentWindowHeight) {
            h = parentWindowHeight;
        }
        setWidgetWidth(w);
        setWidgetHeight(h);
    }, [widgetState, workflow, parentWindowHeight, parentWindowWidth, mobileState]);
    // When widgetHeight or widgetWidth is updated per above useEffect
    // 1. Update widget size
    // 2. Post message to parent (which will update the frame size every time a message w/ this info is received)
    useEffect(function () {
        var alwaysAllowedOrigins = getAlwaysAllowedOrigins();
        var partnerOrigins = frameAncestorsToPostMessageOrigins();
        var targetOrigns = alwaysAllowedOrigins.concat(partnerOrigins);
        var widgetContainer = document.getElementById('widget-container-internal');
        if (widgetContainer) {
            if (mobileState === MobileStates.MOBILE) {
                // need to set w/h to 100% on mobile so children can be w/h 100%
                // (desktop we have hardcoded w & h)
                widgetContainer.style.width = "".concat(widgetWidth, "px");
                widgetContainer.style.height = "".concat(widgetHeight, "px");
            }
            var message_1 = JSON.stringify({
                widgetWidth: widgetWidth,
                widgetHeight: widgetHeight,
            });
            targetOrigns.forEach(function (o) {
                try {
                    window.parent.postMessage(message_1, o);
                }
                catch (e) {
                    /*
                      Note postMessage will fail in all cases but 1 correct origin,,
                      because we are posting to ALL origins
                    */
                }
            });
        }
    }, [widgetWidth, widgetHeight, mobileState]);
    return React.createElement(React.Fragment, null, children);
};
